import axios from 'axios';
import config from '../config';

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 15000,
});

// Attach JWT from localStorage on every request
api.interceptors.request.use((req) => {
  const token = localStorage.getItem('cashflow_token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// ---------------------------------------------------------------------------
// Client auth
// ---------------------------------------------------------------------------

/**
 * Exchange a raw magic-link token for a JWT.
 * @param {string} token - Raw token from the magic link URL parameter.
 */
export async function loginWithToken(token) {
  const res = await api.post('/auth/cashflow/login', { token });
  return res.data;
}

/**
 * Dev-only: log in as the auto-created test client (no token needed).
 */
export async function devLogin(clientType = 'hff') {
  const res = await api.post(`/auth/cashflow/dev-login?client_type=${clientType}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Wizard progress (client-facing)
// ---------------------------------------------------------------------------

/** Load the client's saved wizard progress. */
export async function getProgress() {
  const res = await api.get('/wizard/progress');
  return res.data;
}

/** Detect whether the client is on a mobile or desktop device. */
function getDeviceType() {
  const ua = navigator.userAgent || '';
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop';
}

/**
 * Save wizard progress.
 * @param {number} currentStep
 * @param {object} formData
 * @param {boolean} completed
 */
export async function saveProgress(currentStep, formData, completed = false) {
  const res = await api.put('/wizard/progress', {
    current_step: currentStep,
    form_data: formData,
    completed,
    device_type: getDeviceType(),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Statement import (client-facing)
// ---------------------------------------------------------------------------

/** Largest file the import accepts (matches MAX_IMPORT_BYTES on the backend). */
export const MAX_IMPORT_BYTES = 4 * 1024 * 1024;

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

const POLL_EVERY_MS = 1500;
const POLL_FOR_MS = 60000;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Upload a statement or document and get its checked result back.
 *
 * The server parses the file in memory and keeps nothing. When the result
 * still needs the classifier's check, it answers "pending" with a ticket;
 * this polls for up to a minute and then settles for the rules-only
 * fallback. Resolves to { result } or { rejected: message }. The caller
 * applies the result to form data and saves it with saveProgress.
 * @param {File} file - PDF, .xlsx or .csv.
 */
export async function importStatement(file) {
  if (!file.size) throw new Error('This file is empty.');
  if (file.size > MAX_IMPORT_BYTES) throw new Error('This file is larger than 4 MB.');
  const contentBase64 = await readAsBase64(file);
  const res = await api.post(
    '/wizard/import',
    { filename: file.name, content_base64: contentBase64 },
    { timeout: 60000 },
  );
  let body = res.data;
  if (body.status === 'pending') {
    const deadline = Date.now() + POLL_FOR_MS;
    const { ticket, fallback } = body;
    body = { status: 'done', result: fallback };
    while (Date.now() < deadline) {
      await wait(POLL_EVERY_MS);
      try {
        const poll = await api.post('/wizard/import/result', { ticket }, { timeout: 20000 });
        if (poll.data.status !== 'pending') {
          body = poll.data;
          break;
        }
      } catch (err) {
        if (err?.response?.status === 404) break; // the ticket isn't valid: use the fallback
      }
    }
  }
  if (body.status === 'rejected') return { rejected: body.message };
  return { result: body.result };
}

/** Get current client info. */
export async function getMe() {
  const res = await api.get('/wizard/me');
  return res.data;
}

// ---------------------------------------------------------------------------
// Admin (advisor-facing)
// ---------------------------------------------------------------------------

/**
 * Create a new client intake (advisor auth required).
 * @param {object} clientData - { name, email, phone?, notes? }
 * @param {string} advisorToken - Advisor JWT.
 */
export async function createClient(clientData, advisorToken) {
  const res = await axios.post(`${config.apiUrl}/admin/cashflow/clients`, clientData, {
    headers: { Authorization: `Bearer ${advisorToken}` },
  });
  return res.data;
}

/**
 * List all clients for the authenticated advisor.
 * @param {string} advisorToken
 */
export async function listClients(advisorToken) {
  const res = await axios.get(`${config.apiUrl}/admin/cashflow/clients`, {
    headers: { Authorization: `Bearer ${advisorToken}` },
  });
  return res.data;
}

export default api;
